import { useEffect, useRef, useState } from "react";
import theme from "../../theme";
import { REGULA_BASE_URL } from "../../constants";
import type { RegulaImageCaptureProps } from "../../types";
import type { FaceLivenessDetailType, FaceLivenessWebComponent } from "@regulaforensics/vp-frontend-face-components";
import { Box, Button, Dialog, DialogActions, DialogTitle, Typography } from "@mui/material";
import { useAppDispatch } from "../../redux";
import { overrideAttempts } from "./ImageCaptureSlice";
import { setIsLoading } from "../../AppSlice";

export default function RegulaImageCapture(props: RegulaImageCaptureProps) {
  const { isOpen, setSelfie, handleContinue, close, setIsDialogOpen, text, attempts } = props;

  const dispatch = useAppDispatch();

  const containerRef = useRef<HTMLDivElement>(null);
  const componentRef = useRef<FaceLivenessWebComponent>(null);
  const livenessTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const cameraTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const [isApproved, setIsApproved] = useState(false);
  const [processing, setProcessing] = useState<boolean | null>(null);
  const [isHelp, setIsHelp] = useState(false);
  const [isOptions, setIsOptions] = useState(false);
  const [isCameraOpen, setIsCameraOpen] = useState(true);

  const listener = (data: CustomEvent<FaceLivenessDetailType>) => {
    if (data.detail.action === "PRESS_START_BUTTON") {
      if (cameraTimeoutRef.current) clearTimeout(cameraTimeoutRef.current);

      cameraTimeoutRef.current = setTimeout(() => setIsCameraOpen(false), 10000);
    }

    if (data.detail.action === "SERVICE_INITIALIZED" || data.detail.action === "PRESS_RETRY_BUTTON") {
      if (cameraTimeoutRef.current) clearTimeout(cameraTimeoutRef.current);

      setIsCameraOpen(true);
      setProcessing(true);
    }

    if (data.detail.action === "PROCESS_FINISHED") {
      setProcessing(false);
      setIsHelp(false);

      if (data.detail.data?.status === 1 && data.detail.data.response && !data.detail.data.response.status) {
        setSelfie(data.detail.data.response.images[0]);
        setIsApproved(true);
      }
    }

    if (data.detail.action === "CLOSE") {
      setProcessing(false);
      setIsHelp(false);
      close();
    }

    if (data.detail.action === "RETRY_COUNTER_EXCEEDED") {
      dispatch(setIsLoading(true));
      dispatch(overrideAttempts(2));
    }
  };

  useEffect(() => {
    if (processing === null) return;

    if (!processing) {
      if (livenessTimeoutRef.current) clearTimeout(livenessTimeoutRef.current);
    } else {
      setIsOptions(false);

      const timeout = setTimeout(() => {
        setIsHelp(true);
      }, 30000);

      livenessTimeoutRef.current = timeout;
    }

    return () => {
      setIsHelp(false);
      if (livenessTimeoutRef.current) clearTimeout(livenessTimeoutRef.current);
    };
  }, [processing]);

  useEffect(() => {
    const containerCurrent = containerRef.current;

    if (!containerCurrent) return;

    containerCurrent.addEventListener("face-liveness", listener);

    if (isOpen && componentRef.current) {
      componentRef.current.settings = {
        closeDisabled: true,
        locale: text.regulaLocale,
        url: REGULA_BASE_URL,
        recordingProcess: 2,
        retryCount: 2,
        livenessType: 1,
        customization: {
          fontFamily: "Roboto",
          onboardingScreenStartButtonBackground: theme.palette.primary.main,
          onboardingScreenStartButtonBackgroundHover: "#2c387d",
        },
      };
    }

    return () => {
      containerCurrent.removeEventListener("face-liveness", listener);
    };
  }, [isOpen]);

  useEffect(() => {
    if (isApproved) {
      handleContinue();
      setIsDialogOpen(true);
    }
  }, [isApproved]);

  return attempts < 2 ? (
    <>
      <Box
        ref={containerRef}
        sx={{
          display: "flex",
          position: "absolute",
          height: "100%",
          width: "100%",
          top: 0,
          left: 0,
          justifyContent: "center",
          alignItems: "center",
          zIndex: 3,
        }}
      >
        <face-liveness ref={componentRef} />
      </Box>

      <Dialog open={isHelp}>
        <DialogTitle textAlign="center">
          <Typography variant="h3" textAlign="center">
            {text.havingTrouble}
          </Typography>
        </DialogTitle>

        {!isOptions ? (
          <DialogActions sx={{ alignItems: "end", flexDirection: "column", gap: "10px" }}>
            <Button
              fullWidth
              variant="contained"
              color="primary"
              onClick={() => setIsHelp(false)}
              sx={{ fontSize: "25px" }}
            >
              {text.yes}
            </Button>

            <Button
              fullWidth
              variant="contained"
              color="secondary"
              onClick={() => setIsOptions(true)}
              sx={{ fontSize: "25px" }}
            >
              {text.no}
            </Button>
          </DialogActions>
        ) : (
          <DialogActions sx={{ alignItems: "end", flexDirection: "column", gap: "10px" }}>
            <Button
              fullWidth
              variant="contained"
              color="primary"
              onClick={() => {
                dispatch(setIsLoading(true));
                dispatch(overrideAttempts(2));
              }}
              sx={{ fontSize: "25px" }}
            >
              {text.manualPhoto}
            </Button>

            <Button
              fullWidth
              variant="contained"
              color="secondary"
              onClick={() => setIsHelp(false)}
              sx={{ fontSize: "25px" }}
            >
              {text.tryAgainShort}
            </Button>
          </DialogActions>
        )}
      </Dialog>

      <Dialog open={!isCameraOpen}>
        <DialogTitle textAlign="center">
          <Typography sx={{ fontSize: "40px" }}>
            {text.cantCapture}
          </Typography>
        </DialogTitle>

        <DialogActions sx={{ alignItems: "end", flexDirection: "column", gap: "10px" }}>
          <Button
            fullWidth
            variant="contained"
            color="primary"
            onClick={() => {
              dispatch(setIsLoading(true));
              dispatch(overrideAttempts(2));
            }}
            sx={{ fontSize: "25px" }}
          >
            {text.manualPhoto}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  ) : null;
}
